/* ==========================================================================
   CREATE YOUR SET
   One shirt + one pair of pants (+ optional shoes), previewed together, sized
   separately, added to the cart as a single action through the theme's own
   cart pipeline so the drawer and counters refresh exactly like any other
   add. Ready-made sets load every piece into the builder in one click. The
   set discount is mirrored here for display; Shopify's automatic discount
   applies the real one.
   ========================================================================== */

(function () {
  'use strict';

  function define(name, constructor) {
    if (!customElements.get(name)) customElements.define(name, constructor);
  }

  const desktop = window.matchMedia('(min-width: 1024px)');
  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Shirt and pants make a set; shoes are the optional third piece. */
  const SLOTS = ['top', 'bottom', 'shoes'];
  const REQUIRED = ['top', 'bottom'];

  class SetBuilder extends HTMLElement {
    connectedCallback() {
      this.slots = {};
      SLOTS.forEach((slot) => {
        this.slots[slot] = { card: null, sizes: [], variantId: null };
      });
      this.pct = parseInt(this.dataset.discountPercent || '0', 10) || 0;
      this.applyingPreset = false;

      this.addBtn = this.querySelector('[data-setb-add]');
      this.msg = this.querySelector('[data-setb-msg]');
      this.totalRow = this.querySelector('[data-setb-total-row]');
      this.totalEl = this.querySelector('[data-setb-total]');
      this.fullEl = this.querySelector('[data-setb-full]');
      this.discountRow = this.querySelector('[data-setb-discount-row]');
      this.discountEl = this.querySelector('[data-setb-discount]');
      this.preview = this.querySelector('[data-setb-preview]');
      this.addLabel = this.addBtn ? this.addBtn.textContent.trim() : '';

      this.querySelectorAll('[data-setb-item]').forEach((card) => {
        card.addEventListener('click', () => this.select(card));
      });

      this.querySelectorAll('[data-preset-pick]').forEach((button) => {
        button.addEventListener('click', () => this.applyPreset(button.closest('[data-setb-preset]')));
      });

      this.querySelectorAll('[data-setb-line]').forEach((line) => {
        const select = line.querySelector('[data-line-size]');
        select.addEventListener('change', () => {
          const slot = line.dataset.setbLine;
          this.slots[slot].variantId = select.value || null;
          this.sync();
        });
      });

      this.querySelectorAll('[data-line-remove]').forEach((button) => {
        button.addEventListener('click', () => {
          this.clearSlot(button.dataset.lineRemove);
          this.forgetPreset();
          this.sync();
        });
      });

      if (this.addBtn) this.addBtn.addEventListener('click', () => this.addSet());
    }

    /* Ready-made sets --------------------------------------------------- */

    cardFor(slot, handle, color) {
      if (!handle) return null;
      const cards = Array.from(
        this.querySelectorAll('[data-setb-item][data-slot="' + slot + '"][data-handle="' + handle + '"]')
      );
      if (!cards.length) return null;
      if (color) {
        const exact = cards.find((card) => card.dataset.color === color);
        if (exact) return exact;
      }
      return cards[0];
    }

    applyPreset(preset) {
      if (!preset) return;
      const top = this.cardFor('top', preset.dataset.topHandle, preset.dataset.topColor);
      const bottom = this.cardFor('bottom', preset.dataset.bottomHandle, preset.dataset.bottomColor);
      const wantsShoes = Boolean(preset.dataset.shoesHandle);
      const shoes = wantsShoes ? this.cardFor('shoes', preset.dataset.shoesHandle, preset.dataset.shoesColor) : null;

      this.applyingPreset = true;
      if (top) this.select(top);
      if (bottom) this.select(bottom);
      if (shoes) {
        this.select(shoes);
      } else {
        /* A two-piece set replaces whatever shoes were chosen before. */
        this.clearSlot('shoes');
      }
      this.applyingPreset = false;

      this.querySelectorAll('[data-setb-preset]').forEach((el) => {
        el.classList.toggle('is-chosen', el === preset);
      });

      this.sync();
      if (!top || !bottom || (wantsShoes && !shoes)) {
        this.note('One piece of this set is not available right now. Pick another below.', true);
      }

      this.revealPreview();
    }

    forgetPreset() {
      this.querySelectorAll('[data-setb-preset].is-chosen').forEach((el) => el.classList.remove('is-chosen'));
    }

    /* Bring the preview into view after a preset, unless it is already on
       screen (the sticky column on desktop usually is). */
    revealPreview() {
      if (!this.preview) return;
      const rect = this.preview.getBoundingClientRect();
      const onScreen = rect.top >= 0 && rect.top < window.innerHeight * 0.6;
      if (desktop.matches && onScreen) return;
      this.preview.scrollIntoView({
        behavior: reduceMotion.matches ? 'auto' : 'smooth',
        block: 'start',
      });
    }

    /* Selection ---------------------------------------------------------- */

    select(card) {
      const slot = card.dataset.slot;
      const state = this.slots[slot];
      if (!state) return;

      /* One selection per group. */
      this.querySelectorAll('[data-setb-item][data-slot="' + slot + '"]').forEach((el) => {
        el.setAttribute('aria-selected', el === card ? 'true' : 'false');
      });
      state.card = card;
      state.variantId = null;

      /* A manual pick means the shopper left the ready-made set. */
      if (!this.applyingPreset) this.forgetPreset();

      let sizes = [];
      try {
        sizes = JSON.parse(card.dataset.sizes || '[]');
      } catch (error) {
        sizes = [];
      }
      state.sizes = sizes;

      const color = card.dataset.color || '';

      /* Preview image. */
      const slotEl = this.querySelector('[data-preview-slot="' + slot + '"]');
      if (slotEl) {
        const img = slotEl.querySelector('img');
        img.classList.remove('is-in');
        img.hidden = false;
        img.src = card.dataset.image;
        img.alt = card.dataset.title + (color ? ', ' + color : '');
        requestAnimationFrame(() => requestAnimationFrame(() => img.classList.add('is-in')));
        slotEl.classList.add('has-item');
      }

      /* Line details and the size select. */
      const line = this.querySelector('[data-setb-line="' + slot + '"]');
      if (line) {
        line.hidden = false;
        line.querySelector('[data-line-title]').textContent = card.dataset.title;
        line.querySelector('[data-line-color]').textContent = color;
        const sep = line.querySelector('[data-line-sep]');
        if (sep) sep.hidden = !color;
        line.querySelector('[data-line-price]').textContent = card.dataset.priceLabel;

        const select = line.querySelector('[data-line-size]');
        const placeholder = select.options[0].textContent;
        select.innerHTML = '';
        const first = document.createElement('option');
        first.value = '';
        first.textContent = placeholder;
        select.append(first);
        sizes.forEach((entry) => {
          const option = document.createElement('option');
          option.value = String(entry.id);
          option.textContent = entry.available ? entry.size : entry.size + ' — sold out';
          option.disabled = !entry.available;
          select.append(option);
        });
        select.value = '';
      }

      this.sync();
    }

    /* Empty a slot again (used for the optional shoes). */
    clearSlot(slot) {
      const state = this.slots[slot];
      if (!state) return;
      state.card = null;
      state.sizes = [];
      state.variantId = null;

      this.querySelectorAll('[data-setb-item][data-slot="' + slot + '"]').forEach((el) => {
        el.setAttribute('aria-selected', 'false');
      });

      const slotEl = this.querySelector('[data-preview-slot="' + slot + '"]');
      if (slotEl) {
        const img = slotEl.querySelector('img');
        img.classList.remove('is-in');
        img.hidden = true;
        img.removeAttribute('src');
        img.alt = '';
        slotEl.classList.remove('has-item');
      }

      const line = this.querySelector('[data-setb-line="' + slot + '"]');
      if (line) line.hidden = true;
    }

    /* Totals ------------------------------------------------------------- */

    sync() {
      const top = this.slots.top;
      const bottom = this.slots.bottom;
      const shoes = this.slots.shoes;

      if (top.card && bottom.card && this.totalRow) {
        const topCents = parseInt(top.card.dataset.priceCents, 10) || 0;
        const bottomCents = parseInt(bottom.card.dataset.priceCents, 10) || 0;
        const shoesCents = shoes.card ? parseInt(shoes.card.dataset.priceCents, 10) || 0 : 0;
        const full = topCents + bottomCents + shoesCents;
        /* Same integer maths as the Liquid preset cards: floor of pants x pct.
           Shoes are full price. */
        const saving = this.pct > 0 ? Math.floor((bottomCents * this.pct) / 100) : 0;
        const total = full - saving;
        const sample = top.card.dataset.priceLabel;

        this.totalRow.hidden = false;
        if (this.discountRow) this.discountRow.hidden = saving === 0;
        if (this.discountEl) this.discountEl.textContent = '−' + this.formatMoney(saving, sample);
        if (this.fullEl) {
          this.fullEl.hidden = saving === 0;
          this.fullEl.textContent = this.formatMoney(full, sample);
        }
        this.totalEl.textContent = this.formatMoney(total, sample);
      }

      if (this.addBtn) {
        const required = REQUIRED.every((slot) => this.slots[slot].variantId);
        const shoesReady = !shoes.card || Boolean(shoes.variantId);
        this.addBtn.disabled = !(required && shoesReady);
      }
      this.note('', false);
    }

    /* Reuse the store's own money formatting by swapping the number inside a
       known formatted price, so the total matches the catalogue exactly. */
    formatMoney(cents, sampleLabel) {
      const match = sampleLabel ? sampleLabel.match(/\d[\d.,]*/) : null;
      if (!match) return (cents / 100).toFixed(2);

      /* Read the separators off the sample: "1,234.00", "1.234,00" or "85". */
      const sample = match[0];
      const decimal = sample.match(/([.,])(\d{1,2})$/);
      const decimalSep = decimal ? decimal[1] : '';
      const thousandsSep = decimalSep === ',' ? '.' : ',';

      const whole = String(Math.floor(cents / 100)).replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSep);
      const fraction = decimal ? decimalSep + String(cents % 100).padStart(2, '0') : '';
      return sampleLabel.replace(sample, whole + fraction);
    }

    /* Add ---------------------------------------------------------------- */

    addSet() {
      const items = [];
      SLOTS.forEach((slot) => {
        const id = parseInt(this.slots[slot].variantId, 10);
        if (id) items.push({ id: id, quantity: 1 });
      });
      if (!REQUIRED.every((slot) => this.slots[slot].variantId)) return;

      this.addBtn.disabled = true;
      this.addBtn.textContent = this.addBtn.dataset.busyLabel || '…';
      this.note('', false);

      const done = () => {
        this.addBtn.textContent = this.addLabel;
        this.sync();
      };

      if (window.Theme && window.Theme.cart && window.Theme.cart.add) {
        window.Theme.cart
          .add(items)
          .then(() => {
            done();
            if (window.Theme.openOverlay) window.Theme.openOverlay('cart-drawer');
          })
          .catch((error) => {
            done();
            this.note(error.message || 'Something went wrong. Try again.', true);
          });
        return;
      }

      /* Fallback when the theme bundle is unavailable: plain Cart API. */
      fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: items }),
      })
        .then((response) => {
          if (!response.ok) throw new Error('Something went wrong. Try again.');
          window.location.href = '/cart';
        })
        .catch((error) => {
          done();
          this.note(error.message, true);
        });
    }

    note(text, isError) {
      if (!this.msg) return;
      this.msg.hidden = !text;
      this.msg.textContent = text;
      this.msg.classList.toggle('is-error', Boolean(isError));
    }
  }

  define('set-builder', SetBuilder);
})();
